import type { SizingExperimentResponseDto } from "@babackapp/private-api-client/types";
import useUpdateSizingExperiment from "@babackapp/private-api-client/client/use-update-sizing-experiment";
import { Button } from "@babackapp/ui/components/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@babackapp/ui/components/dialog";
import { Input } from "@babackapp/ui/components/input";
import { Label } from "@babackapp/ui/components/label";
import { useEffect, useState } from "react";

interface EditExperimentDialogProps {
  experiment: SizingExperimentResponseDto | null;
  shopId: string;
  onClose: () => void;
}

export function EditExperimentDialog({
  experiment,
  shopId,
  onClose,
}: EditExperimentDialogProps) {
  const [name, setName] = useState("");
  const [splitPercent, setSplitPercent] = useState("");
  const mutation = useUpdateSizingExperiment(shopId);

  useEffect(() => {
    if (experiment) {
      setName(experiment.name);
      setSplitPercent(experiment.splitPercent.toString());
    }
  }, [experiment]);

  function handleSave() {
    if (!experiment) return;
    const split = parseInt(splitPercent, 10);
    if (!name.trim()) return;

    mutation.mutate(
      {
        experimentId: experiment.experimentId,
        name: name.trim(),
        ...(!experiment.isActive &&
          Number.isFinite(split) && { splitPercent: split }),
      },
      { onSuccess: onClose },
    );
  }

  return (
    <Dialog open={!!experiment} onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit experiment</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="edit-name">Name</Label>
            <Input
              id="edit-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={120}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit-split">
              Split %{" "}
              {experiment?.isActive && (
                <span className="text-muted-foreground font-normal">
                  (locked while active)
                </span>
              )}
            </Label>
            <Input
              id="edit-split"
              type="number"
              min={0}
              max={100}
              step={1}
              value={splitPercent}
              onChange={(e) => setSplitPercent(e.target.value)}
              disabled={experiment?.isActive}
            />
          </div>
          <div className="flex justify-end">
            <Button
              onClick={handleSave}
              disabled={mutation.isPending || !name.trim()}
            >
              {mutation.isPending ? "Saving..." : "Save"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
